import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InlineStudentCell from './InlineStudentCell';
import DossierStatusPath from './DossierStatusPath';
import { fieldsFromTemplate } from '../shared/exchanges';
import DocumentViewer, { documentUrl, documentsArchiveUrl, previewItems } from './DocumentViewer';
import { Modal, Notice, formatDate, Icon } from './components';

export default function DossierDialog({ id, api, onClose, onDeleted, onUpdated, onLoaded, embedded = false, active = true, closeGuards, latestStudent }) {
  const childGuard = useRef(null);
  const deletingRef = useRef(false);
  const savingStatusRef = useRef(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const loadedRef = useRef(onLoaded);
  loadedRef.current = onLoaded;
  const editGuards = useRef(new Map());
  const [previewIndex, setPreviewIndex] = useState(null);
  const [student, setStudent] = useState(null);
  const [error, setError] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api(`/students/${encodeURIComponent(id)}`, { signal: controller.signal }).then(data => { setStudent(data.student); loadedRef.current?.(data.student); }).catch(e => { if (e.name !== 'AbortError') setError(e.message); });
    return () => controller.abort();
  }, [api, id]);
  useEffect(() => {
    if (latestStudent && previewIndex === null) setStudent(latestStudent);
  }, [latestStudent, previewIndex]);
  const leaveFields = useCallback(() => {
    const states = [...editGuards.current.values()].map(read => read());
    if (savingStatusRef.current || states.some(state => state.busy)) return false;
    return !states.some(state => state.dirty) || window.confirm('Quitter sans enregistrer les champs modifiés ?');
  }, []);
  const fields = useMemo(() => fieldsFromTemplate({ criteres: (student?.dossier?.data || []).flatMap(entry => entry.criteres || []) }), [student]);
  function updatedStudent(updated) { setStudent(updated); onUpdated?.(updated); }
  function renderField(field, target, valueProperty = 'value') {
    return <InlineStudentCell as="div" row={student} field={field} fields={fields} target={target} valueProperty={valueProperty}
      api={api} onUpdated={updatedStudent} disabled={deleting || confirmDelete || savingStatus} editGuards={editGuards} />;
  }
  function openPreview(index) {
    if (deletingRef.current || !leaveFields()) return;
    setPreviewIndex(index);
  }
  function requestClose() {
    if (closeGuards || (!deletingRef.current && leaveFields())) onClose();
  }
  useEffect(() => {
    if (!closeGuards) return undefined;
    const guards = closeGuards.current;
    guards.set(id, () => !deletingRef.current && (!childGuard.current || childGuard.current()) && leaveFields());
    return () => guards.delete(id);
  }, [closeGuards, id, leaveFields]);
  async function changeStatus(status) {
    if (!student || status === student.status || savingStatusRef.current || deletingRef.current || confirmDelete) return;
    if ([...editGuards.current.values()].some(read => read().busy)) {
      setError('Veuillez attendre la fin de l’enregistrement du champ avant de changer le statut.');
      return;
    }
    savingStatusRef.current = true; setSavingStatus(true); setError('');
    try {
      const result = await api(`/students/${encodeURIComponent(id)}`, {
        method: 'PATCH', body: { changes: [], status, expectedUpdatedAt: student.updatedAt }
      });
      updatedStudent(result.student);
    } catch (e) { setError(e.message); }
    finally { savingStatusRef.current = false; setSavingStatus(false); }
  }
  async function copyToken() {
    try { await navigator.clipboard.writeText(student.token); setCopied(true); }
    catch { setError('La copie n’a pas été autorisée. Affichez le token pour le copier manuellement.'); }
  }
  async function removeDossier() {
    if (savingStatusRef.current || deletingRef.current || [...editGuards.current.values()].some(read => read().busy)) return;
    setError('');
    deletingRef.current = true; setDeleting(true);
    try {
      const result = await api(`/students/${encodeURIComponent(id)}`, { method: 'DELETE', body: {} });
      onDeleted(result);
    } catch (error) {
      setError(error.message);
      deletingRef.current = false; setDeleting(false);
    }
  }
  const pieces = student ? previewItems(student) : [];

  if (student && previewIndex !== null) return <DocumentViewer embedded={embedded} active={active} closeGuard={childGuard} student={student} api={api} onUpdated={updated => { setStudent(updated); onUpdated?.(updated); }} initialIndex={previewIndex} onClose={() => setPreviewIndex(null)} />;
  return <Modal embedded={embedded} title="Dossier de l’étudiant" wide busy={deleting || savingStatus} onClose={requestClose}>
    <Notice>{error}</Notice>
    {!student && !error && <div className="ex-empty-small" role="status">Chargement du dossier…</div>}
    {student && <>
      <div className="ex-student-heading"><span className="ex-avatar ex-avatar--large">{student.firstName.charAt(0)}{student.lastName.charAt(0)}</span><div><h3>{student.firstName} {student.lastName}</h3><p className="ex-muted">{student.session}</p></div></div>
      <DossierStatusPath status={student.status} onChange={changeStatus} disabled={deleting || confirmDelete || savingStatus} busy={savingStatus} />
      {savingStatus && <p className="ex-sr-only" role="status">Enregistrement du statut…</p>}
      {student.status === 'in_progress' && <div className="ex-dossier-progress">
        <div className="ex-progress-heading"><span>Avancement</span><strong>{student.progress} %</strong></div>
        <progress max={100} value={student.progress} aria-label="Avancement du dossier" />
      </div>}
      <section className="ex-detail-card"><h3>Informations principales</h3><dl>
        <div><dt>Nom</dt><dd>{renderField('lastName')}</dd></div><div><dt>Prénom</dt><dd>{renderField('firstName')}</dd></div><div><dt>E-mail</dt><dd>{renderField('email')}</dd></div><div><dt>Type d’inscription</dt><dd>Étudiant en échange</dd></div><div><dt>Session</dt><dd>{student.session}</dd></div>
        <div><dt>Créé le</dt><dd>{formatDate(student.createdAt)}</dd></div><div><dt>Mis à jour le</dt><dd>{formatDate(student.updatedAt)}</dd></div>
      </dl></section>
      <section className="ex-detail-card"><h3>Dossier d’inscription en échange</h3>
        <p className="ex-muted">Les réponses et les pièces restent consultables ici après la soumission. Le lien personnel de l’étudiant affiche alors uniquement la confirmation d’envoi.</p>
        {(student.dossier?.data || []).map(entry => <div key={entry.id}>
          {(entry.criteres || []).filter(critere => Number(critere.id) !== 50).map(critere => <div key={critere.id} className="ex-dossier-section"><h3>{critere.nom}</h3>
            {(critere.sousCriteres || []).map(section => <dl key={section.id}>
              {(section.questions || []).filter(question => !['autre', 'paiement'].includes(question.type)).map(question => {
                const target = { entryId: entry.id, critereId: critere.id, sectionId: section.id, questionId: question.id };
                return <div key={question.id}><dt>{question.etiquette || question.nom}</dt><dd>
                  {renderField(`question:${question.id}`, target)}
                  {question.type === 'search' && <div className="ex-inline-postcode"><span>Code postal</span>{renderField(`question:${question.id}`, target, 'code_postal')}</div>}
                </dd></div>;
              })}
            </dl>)}
          </div>)}
        </div>)}
      </section>
      <section className="ex-detail-card"><h3>Pièces du dossier ({student.documents?.length || 0})</h3>
        {Boolean(student.documents?.length) && <a className="ex-button" href={documentsArchiveUrl(student.id)} download><Icon name="download" size={16} />Tout télécharger (.zip)</a>}
        {pieces.length ? <ul className="ex-document-list">{pieces.map((item, index) => <li key={item.key}>
          <Icon name="folder" size={18} /><button className="ex-button ex-document-open" onClick={() => openPreview(index)} aria-label={`${item.document ? 'Voir' : 'Ajouter'} ${item.label}`}>{item.document?.name || item.label}</button>
          {item.document ? <><a className="ex-button" href={documentUrl(student.id, item.document.name)} download aria-label={`Télécharger ${item.document.name}`}><Icon name="download" size={16} /></a><small>{Math.max(1, Math.ceil(item.document.size / 1024))} Ko</small></> : <small>Non fournie · Ajouter</small>}
        </li>)}</ul> : <p className="ex-muted">Aucune pièce prévue dans ce dossier.</p>}
      </section>
      <details className="ex-token-details"><summary>Token du dossier</summary><p className="ex-help">Identifiant confidentiel associé au dossier dès sa création.</p><div className="ex-token-row"><input aria-label="Token du dossier" type={showToken ? 'text' : 'password'} readOnly value={student.token} autoComplete="off" /><button className="ex-button" onClick={() => setShowToken(previous => !previous)}>{showToken ? 'Masquer' : 'Afficher'}</button><button className="ex-button" onClick={copyToken}>{copied ? <><Icon name="check" size={16} />Copié</> : 'Copier'}</button></div></details>
      {confirmDelete && <div className="ex-delete-confirm" role="alertdialog" aria-labelledby={`exchange-delete-title-${id}`}>
        <h3 id={`exchange-delete-title-${id}`}>Supprimer le dossier de {student.firstName} {student.lastName} ?</h3>
        <p>Le dossier, ses réponses, son token et toutes ses pièces seront supprimés définitivement.</p>
        <div className="ex-actions"><button className="ex-button" disabled={deleting} onClick={() => setConfirmDelete(false)}>Annuler</button><button className="ex-button ex-button--danger-confirm" disabled={deleting} onClick={removeDossier}>{deleting ? 'Suppression…' : 'Confirmer la suppression'}</button></div>
      </div>}
    </>}
    <div className="ex-modal-footer ex-modal-footer--split">
      {student && !confirmDelete && <button className="ex-button ex-button--danger" disabled={deleting || savingStatus} onClick={() => { if (leaveFields()) setConfirmDelete(true); }}>Supprimer le dossier</button>}
      <div className="ex-actions">{student && !confirmDelete && <a className="ex-button ex-button--primary" href={`/?tkn=${encodeURIComponent(student.token)}`} target="_blank" rel="noopener noreferrer">Ouvrir à la place de l’étudiant<Icon name="arrow" size={16} /></a>}<button className="ex-button" disabled={deleting || savingStatus} onClick={requestClose}>Fermer</button></div>
    </div>
  </Modal>;
}
